import React, { useState } from 'react';
import StudentIDCardTemplate from './id-card/StudentIDCardTemplate';
import { downloadIdCardPdf, printIdCard } from '../utils/idCardExportService';
import { idCardImageCacheKey } from '../utils/idCardCardData';
import useActiveIdCardTemplate from '../hooks/useActiveIdCardTemplate';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Download, Printer, IdCard, Sparkles, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { ID_CARD_DESIGNS } from '../utils/idCardTheme';
import { cardFormatLabel } from '../utils/idCardFormats';

export { default as StudentIDCardTemplate } from './id-card/StudentIDCardTemplate';

export default function StudentIdCard({
  student,
  school,
  photoUrl,
  imageCacheKey = '',
  assetOverrides = null,
  previewVersion = 0,
  compact = false,
  showToolbar = true,
}) {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const schoolId = school?.id || student?.school_id;

  const {
    activeTemplate,
    design,
    format,
    loading: templateLoading,
  } = useActiveIdCardTemplate(schoolId);

  const cacheKey = idCardImageCacheKey(school, imageCacheKey);
  const templateLabel = activeTemplate?.name
    || `${ID_CARD_DESIGNS[design]?.label || design} · ${cardFormatLabel(format)}`;

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const fn = await downloadIdCardPdf(student, school, photoUrl, {
        format,
        design,
        imageCacheKey: cacheKey,
      });
      toast({ title: 'Downloaded', description: fn });
    } catch (e) {
      toast({ title: 'Failed', description: e?.message || 'PDF error', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = async () => {
    try {
      await printIdCard(student, school, photoUrl, {
        format,
        design,
        imageCacheKey: cacheKey,
      });
    } catch (e) {
      toast({ title: 'Print failed', description: e?.message || 'Could not print', variant: 'destructive' });
    }
  };

  const previewBlock = (
    <StudentIDCardTemplate
      student={student}
      school={school}
      photoUrl={photoUrl}
      imageCacheKey={imageCacheKey}
      assetOverrides={assetOverrides}
      previewVersion={previewVersion}
      format={format}
      design={design}
      compact={compact}
    />
  );

  if (compact) return previewBlock;

  return (
    <Card className="border-orange-200/40 dark:border-orange-900/30 overflow-hidden shadow-sm">
      <div className="h-1 bg-gradient-to-r from-[#FF6B00] via-[#F25C05] to-[#E65100]" />
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <IdCard className="w-5 h-5 text-orange-600" />
          Student ID Card
          <Sparkles className="w-4 h-4 text-orange-400 ml-1" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {templateLoading ? (
            'Loading school template…'
          ) : (
            <>
              Template: <span className="font-medium text-foreground">{templateLabel}</span>
              {' · '}
              Change in Settings → ID Card Setup
            </>
          )}
        </p>
      </CardHeader>
      <CardContent className="space-y-6 overflow-x-hidden">
        {showToolbar && (
          <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl bg-muted/40 border border-border/60">
            <Button
              onClick={downloadPdf}
              disabled={downloading || templateLoading}
              className="bg-gradient-to-r from-[#FF6B00] to-[#E65100] hover:opacity-95 shadow-md"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? 'Generating…' : 'Download PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              disabled={templateLoading}
              className="border-orange-200"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            {templateLoading && (
              <span className="text-sm text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading template…
              </span>
            )}
          </div>
        )}
        {previewBlock}
      </CardContent>
    </Card>
  );
}
